import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Grid, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  getAssetNotes,
  createNote,
  deleteCategory,
  updateCategory,
} from "../../api/asset";
import { handleAxiosError } from "../../config/action-config";
import { assetNotesTable as tableDef } from "../../config/grid-columns-defs";
import DeleteAlertDialog from "../../component/DeleteAlertDialog";
import UpdateDialog from "../../component/UpdateDialog";
import CreateDialog from "../../component/CreateDialog";
import { showAlert } from "../../actions/alert";
import { Controller, useForm } from "react-hook-form";
import BetterControlledTable from "../../component/BetterControlledTable";
import { useParams } from "react-router-dom";

const emptyUpdateData = {
  category: "",
  description: "",
};

const emptynewNoteData = {
  asset_id: "",
  note: "",
};

export default function NoteList() {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();
  const user = useSelector((state) => state.user);
  const dialogAction = useSelector((state) => state.dialog);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [updateDialogOpen, setUpdateDialogeOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(-1);
  const [updateId, setUpdateId] = useState(-1);

  const [updateData, setUpdateData] = useState(emptyUpdateData);
  const [newNoteData, setNewNoteData] = useState(emptynewNoteData);

  const onNewNoteSubmit = (newNoteData) => {
    const combineData = { ...newNoteData, asset_id: params.id };
    createNote(combineData, user.token)
      .then((res) => {
        dispatch(showAlert(res.data.msg));
        setNewNoteData(emptynewNoteData);
        refreshTable();
        setCreateDialogOpen(false);
      })
      .catch((err) => {
        handleAxiosError(err, dispatch);
        setCreateDialogOpen(false);
      });
  };

  useEffect(() => {
    if (!dialogAction.type) return;
    switch (dialogAction.type) {
      case "EDIT": {
        setUpdateDialogeOpen(true);
        const up = getUpdatableData(dialogAction.data);
        setUpdateData(up);
        break;
      }
      case "DELETE":
        setDeleteDialogOpen(true);
        break;
      case "VIEW":
        dispatch({ type: "" });
        navigate(`${dialogAction.data.id}`);
        break;
      default:
        break;
    }
  }, [dialogAction]);

  useEffect(() => {
    if (deleteId === -1) return;
    deleteCategory(deleteId, user.token)
      .then((res) => {
        dispatch(showAlert(res.data.msg));
        setDeleteId(-1);
        refreshTable();
        setDeleteDialogOpen(false);
      })
      .catch((err) => {
        handleAxiosError(err, dispatch);
        setDeleteId(-1);
        setDeleteDialogOpen(false);
      });
  }, [deleteId]);

  useEffect(() => {
    if (updateId === -1) return;
    updateCategory(updateId, updateData, user.token)
      .then((res) => {
        dispatch(showAlert(res.data.msg));
        setUpdateId(-1);
        setUpdateData(emptyUpdateData);
        refreshTable();
        setUpdateDialogeOpen(false);
      })
      .catch((err) => {
        handleAxiosError(err, dispatch);
        setUpdateId(-1);
        setUpdateDialogeOpen(false);
      });
  }, [updateId]);

  const changeUpdateData = (e) => {
    setUpdateData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  useEffect(() => {
    if (!createDialogOpen) {
      setNewNoteData(emptynewNoteData);
    }
  }, [createDialogOpen]);

  const [categoryTableRefreshCount, setCategoryTableRefreshCount] = useState(0);

  async function getNoteTableData(limit, offset) {
    try {
      const { data } = await getAssetNotes(
        params.id,
        { sortBy: "ASC", limit, offset },
        user.token
      );
      return {
        rows: data.Notes,
        total: data.total,
        error: false,
      };
    } catch (error) {
      handleAxiosError(error, dispatch);
      return { error: true };
    } finally {
      // setLoading(false);
    }
  }

  function refreshTable(e) {
    e?.preventDefault();
    setCategoryTableRefreshCount((prev) => prev + 1);
  }

  return (
    <Box className="pmt-container-box">
      <Box p={1}>
        <Button
          variant="contained"
          style={{ padding: "5px 7px" }}
          onClick={() => {
            setCreateDialogOpen(true);
          }}
          className="blue-button"
        >
          Add Note
        </Button>
      </Box>
      <br />
      <div className=" pmt-controll-table">
        <BetterControlledTable
          hasActionButtons={false}
          getTableRows={getNoteTableData}
          columns={tableDef}
          perPage={10}
          refreshCounter={categoryTableRefreshCount}
        />
      </div>

      <DeleteAlertDialog
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        setDeleteId={setDeleteId}
        rowId={dialogAction.data?.id}
      />
      <UpdateDialog
        open={updateDialogOpen}
        setOpen={setUpdateDialogeOpen}
        setUpdateId={setUpdateId}
        rowId={dialogAction.data?.id}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              margin="dense"
              name="category"
              fullWidth
              value={updateData.category}
              onChange={changeUpdateData}
              label="Category"
              type="text"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              margin="dense"
              name="description"
              fullWidth
              value={updateData.description}
              onChange={changeUpdateData}
              label="Description"
              type="text"
              variant="outlined"
            />
          </Grid>
        </Grid>
      </UpdateDialog>
      <CreateDialog
        open={createDialogOpen}
        setOpen={setCreateDialogOpen}
        onSubmit={handleSubmit(onNewNoteSubmit)}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              margin="dense"
              name="note"
              fullWidth
              label="Note"
              type="text"
              rows={3}
              multiline
              variant="outlined"
              inputRef={register({
                required: "Note is required.",
              })}
              required
              error={Boolean(errors.note)}
              helperText={errors.note?.message}
            />
          </Grid>
        </Grid>
      </CreateDialog>
    </Box>
  );
}

function getUpdatableData(data) {
  return {
    category: data.category,
    description: data.description,
  };
}
